import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Input, Select, Row, Col,
} from 'antd';
import {
  MenuUnfoldOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import { ReactComponent as MenuIcon } from '../../assets/icons/menu-collapse.svg';

import TeamMemberCard from './TeamMemberCard';
import TeamMembersActiveFilterDropdown from './TeamMembersActiveFilterDropdown';

import './teamMembersPanel.less';

export default function TeamMembersPanel({
  users = [],
  selectedUser,
  onSelect,
  teams = [],
  selectedTeamIds = [],
  onTeamChange,
  activeUserFilterSet,
  onActiveUserFilterChange,
  searchValue = '',
  onSearch,
  showSubmissionTabs = false,
  submitView,
  onSubmitViewChange,
  approvalCount = 0,
  loadingId = null,
  collapsed = false,
  onToggleCollapsed,
  submittedView,
  defaultView,
}) {
  const listRef = useRef();
  const itemRefs = useRef(new Map());
  const itemRefCallbacks = useRef({});

  const teamOptions = useMemo(() => (
    [...teams].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  ), [teams]);

  const selectedId = selectedUser?.id;

  useEffect(() => {
    const node = itemRefs.current.get(selectedId);
    node?.scrollIntoView({ block: 'nearest' });
  }, [selectedId, users]);

  const setItemRef = useCallback((id) => {
    if (!itemRefCallbacks.current[id]) {
      itemRefCallbacks.current[id] = (node) => {
        if (node) {
          itemRefs.current.set(id, node);
          return;
        }
        itemRefs.current.delete(id);
      };
    }
    return itemRefCallbacks.current[id];
  }, []);

  const onKeyDown = useCallback((event) => {
    if (event.target.closest('input, textarea, .ant-select')) return;
    if (!users.length) return;

    const currentIndex = users.findIndex((user) => user.id === selectedId);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIndex = currentIndex < 0 ? 0 : Math.min(users.length - 1, currentIndex + 1);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIndex = currentIndex < 0 ? 0 : Math.max(0, currentIndex - 1);
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = users.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const nextUser = users[nextIndex];
    if (!nextUser || loadingId) return;
    if (nextUser.id !== selectedId) onSelect(nextUser);
    itemRefs.current.get(nextUser.id)?.focus();
  }, [users, selectedId, loadingId, onSelect]);

  if (collapsed) {
    return (
      <div className="team-members-panel is-collapsed">
        <button
          type="button"
          className="team-members-icon-button"
          onClick={onToggleCollapsed}
          aria-label="Expand team members"
          title="Expand team members"
        >
          <MenuUnfoldOutlined />
        </button>
      </div>
    );
  }

  return (
    <div className="team-members-panel">
      <div className="team-members-panel-header">
        <span>Team Members</span>
        <button
          type="button"
          className="team-members-icon-button"
          onClick={onToggleCollapsed}
          aria-label="Collapse team members"
          title="Collapse team members"
        >
          <MenuIcon />
        </button>
      </div>

      <div className="team-members-panel-controls">
        <Select
          mode="multiple"
          className="team-members-team-select"
          value={selectedTeamIds}
          onChange={onTeamChange}
          dropdownMatchSelectWidth={false}
          placeholder="All teams"
          maxTagCount="responsive"
          allowClear
          optionFilterProp="children"
        >
          {teamOptions.map((team) => (
            <Select.Option key={team.id} value={team.id}>
              {team.name}
            </Select.Option>
          ))}
        </Select>

        <Row gutter={8} wrap={false} className="team-members-search-row">
          <Col flex="1 1 0">
            <Input
              className="team-members-searchbar"
              allowClear
              placeholder="Search team member"
              value={searchValue}
              onChange={(event) => onSearch(event.target.value)}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col flex="0 0 32px">
            <TeamMembersActiveFilterDropdown
              activeUserFilterSet={activeUserFilterSet}
              onActiveUserFilterChange={onActiveUserFilterChange}
            />
          </Col>
        </Row>

        {showSubmissionTabs && (
          <div className="team-members-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={submitView === defaultView}
              className={submitView === defaultView ? 'is-active' : ''}
              onClick={() => onSubmitViewChange(defaultView)}
            >
              All
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={submitView === submittedView}
              className={submitView === submittedView ? 'is-active' : ''}
              onClick={() => onSubmitViewChange(submittedView)}
            >
              Submitted
              {approvalCount ? (
                <span className="team-members-tab-count">{approvalCount}</span>
              ) : null}
            </button>
          </div>
        )}
      </div>

      <div
        className="team-members-panel-list"
        ref={listRef}
        role="listbox"
        tabIndex={0}
        aria-label="Team members"
        onKeyDown={onKeyDown}
      >
        {users.length ? users.map((user) => (
          <TeamMemberCard
            key={user.id}
            item={user}
            selected={user.id === selectedId}
            loading={loadingId === user.id}
            onClick={() => {
              if (loadingId) return;
              onSelect(user);
            }}
            itemRef={setItemRef(user.id)}
          />
        )) : (
          <div className="team-members-empty">No team members</div>
        )}
      </div>
      <div className="team-members-panel-footer">
        <div>
          <span>Navigate</span>
          <span>↑ ↓ or ← →</span>
        </div>
        <div>
          <span>First/Last</span>
          <span>Home/End</span>
        </div>
      </div>
    </div>
  );
}

TeamMembersPanel.propTypes = {
  users: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  })),
  selectedUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onSelect: PropTypes.func.isRequired,
  teams: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  })),
  selectedTeamIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onTeamChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  activeUserFilterSet: PropTypes.instanceOf(Set).isRequired,
  onActiveUserFilterChange: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
  onSearch: PropTypes.func.isRequired,
  showSubmissionTabs: PropTypes.bool,
  submitView: PropTypes.string,
  onSubmitViewChange: PropTypes.func.isRequired,
  approvalCount: PropTypes.number,
  loadingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  collapsed: PropTypes.bool,
  onToggleCollapsed: PropTypes.func.isRequired,
  submittedView: PropTypes.string.isRequired,
  defaultView: PropTypes.string.isRequired,
};

TeamMembersPanel.defaultProps = {
  users: [],
  selectedUser: undefined,
  teams: [],
  selectedTeamIds: [],
  searchValue: '',
  showSubmissionTabs: false,
  submitView: undefined,
  approvalCount: 0,
  loadingId: null,
  collapsed: false,
};
